import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  FolderTree,
  Plus,
  Users,
  KeyRound,
  ChevronDown,
  ChevronRight,
  Trash2,
  Edit2,
  X,
  Check,
  Search,
} from 'lucide-react';
import { cn } from '@lib/utils';
import { departmentsService } from '@services/departments.service';
import { permissionsService } from '@services/permissions.service';
import type { Department, Permission } from '@apptypes/index';
import { PageGuard } from '@components/PageGuard';
import { PermissionGate } from '@components/PermissionGate';
import { usePermission } from '@hooks/usePermission';

// ── Create / Edit modal ───────────────────────────────────────────────────────

function DepartmentFormModal({
  dept,
  onClose,
}: {
  dept?: Department;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(dept?.name ?? '');
  const [description, setDescription] = useState(dept?.description ?? '');

  const createMutation = useMutation({
    mutationFn: () => departmentsService.create({ name: name.trim(), description: description.trim() || undefined }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['departments'] });
      onClose();
    },
  });

  const updateMutation = useMutation({
    mutationFn: () => departmentsService.update(dept!.departmentId, { name: name.trim(), description: description.trim() || undefined }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['departments'] });
      onClose();
    },
  });

  const isPending = createMutation.isPending || updateMutation.isPending;
  const error = createMutation.error || updateMutation.error;

  return (
    <div className="fixed inset-0 bg-[rgba(15,61,30,0.45)] flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl shadow-lift w-full max-w-lg p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-[20px] font-medium tracking-[-0.02em] text-[var(--text-primary)]">
            {dept ? 'Edit Department' : 'Create Department'}
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center text-[var(--text-muted)] hover:bg-[var(--bg-base)] hover:text-[var(--text-primary)] transition-colors"
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] mb-1.5">
              Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Customer Support"
              className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-3 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors"
            />
          </div>
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] mb-1.5">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What does this department do?"
              rows={3}
              className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-3 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors resize-none"
            />
          </div>

          {error && (
            <p className="text-[13px] text-[var(--state-danger)]">
              {(error as any)?.message ?? 'Something went wrong.'}
            </p>
          )}
        </div>

        <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-[var(--border-subtle)]">
          <button
            onClick={onClose}
            className="inline-flex items-center gap-2 rounded-full border border-[var(--border-default)] bg-white px-5 py-2.5 text-[14px] font-medium text-[var(--text-primary)] transition-all duration-200 hover:border-[var(--border-strong)]"
          >
            Cancel
          </button>
          <button
            onClick={() => dept ? updateMutation.mutate() : createMutation.mutate()}
            disabled={!name.trim() || isPending}
            className="inline-flex items-center gap-2 rounded-full bg-[var(--brand-forest)] px-5 py-2.5 text-[14px] font-semibold text-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled: hover:brightness-110 hover:shadow-[0_12px_32px_rgba(124,179,66,0.45)] active:brightness-95"
          >
            {isPending ? 'Saving...' : dept ? 'Save Changes' : 'Create Department'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Permissions panel ─────────────────────────────────────────────────────────

function DeptPermissionsPanel({ dept }: { dept: Department }) {
  const queryClient = useQueryClient();
  const { hasPermission } = usePermission();
  const canEdit = hasPermission('permissions.manage_departments');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(
    new Set(dept.permissions.map((p) => p.permissionId))
  );
  const [dirty, setDirty] = useState(false);

  const { data: allPerms = [], isLoading } = useQuery({
    queryKey: ['permissions'],
    queryFn: () => permissionsService.list(),
  });

  const saveMutation = useMutation({
    mutationFn: () => departmentsService.setPermissions(dept.departmentId, Array.from(selected)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['departments'] });
      setDirty(false);
    },
  });

  const toggle = (permId: string) => {
    if (!canEdit) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(permId)) next.delete(permId);
      else next.add(permId);
      return next;
    });
    setDirty(true);
  };

  const filtered = allPerms.filter(
    (p) => !search || p.code.includes(search.toLowerCase()) || p.name.toLowerCase().includes(search.toLowerCase())
  );
  const grouped = filtered.reduce<Record<string, Permission[]>>((acc, p) => {
    const mod = p.code.split('.')[0] ?? 'other';
    if (!acc[mod]) acc[mod] = [];
    acc[mod].push(p);
    return acc;
  }, {});

  if (isLoading) {
    return (
      <div className="px-6 py-4 border-t border-[var(--border-subtle)] bg-[var(--bg-base)]">
        <p className="text-[13px] text-[var(--text-muted)]">Loading permissions...</p>
      </div>
    );
  }

  return (
    <div className="border-t border-[var(--border-subtle)] bg-[var(--bg-base)]">
      <div className="px-6 py-5">
        <div className="flex items-center justify-between mb-4">
          <p className="text-[13px] font-semibold text-[var(--text-primary)]">
            Assign Permissions
            <span className="ml-2 text-[12px] text-[var(--text-muted)] font-normal">{selected.size} selected</span>
          </p>
          {dirty && canEdit && (
            <button
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isPending}
              className="inline-flex items-center gap-1.5 rounded-full bg-[var(--brand-forest)] px-4 py-2 text-[12px] font-semibold text-white transition-all duration-200 disabled:opacity-50 hover:brightness-110 hover:shadow-[0_12px_32px_rgba(124,179,66,0.45)] active:brightness-95"
            >
              <Check className="w-3.5 h-3.5" strokeWidth={1.5} />
              {saveMutation.isPending ? 'Saving...' : 'Save Changes'}
            </button>
          )}
        </div>

        {saveMutation.error && (
          <p className="text-[12px] text-[var(--state-danger)] mb-3">
            {(saveMutation.error as any)?.message ?? 'Failed to save.'}
          </p>
        )}

        <div className="relative mb-4">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)]" strokeWidth={1.5} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search permissions..."
            className="w-full rounded-2xl border border-[var(--border-default)] bg-white pl-10 pr-4 py-3 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors"
          />
        </div>

        <div className="space-y-2.5 max-h-72 overflow-y-auto">
          {Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([mod, perms]) => {
            const allSelected = perms.every((p) => selected.has(p.permissionId));
            const someSelected = perms.some((p) => selected.has(p.permissionId));
            return (
              <div key={mod} className="rounded-2xl border border-[var(--border-subtle)] overflow-hidden bg-white">
                <button
                  onClick={() => {
                    if (!canEdit) return;
                    setSelected((prev) => {
                      const next = new Set(prev);
                      if (allSelected) perms.forEach((p) => next.delete(p.permissionId));
                      else perms.forEach((p) => next.add(p.permissionId));
                      return next;
                    });
                    setDirty(true);
                  }}
                  disabled={!canEdit}
                  className={cn(
                    'w-full flex items-center justify-between px-4 py-2.5 bg-[var(--bg-base)] transition-colors',
                    canEdit ? 'hover:bg-[var(--bg-elevated)]' : 'cursor-default'
                  )}
                >
                  <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">{mod}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] text-[var(--text-muted)]">
                      {perms.filter((p) => selected.has(p.permissionId)).length}/{perms.length}
                    </span>
                    <div className={cn(
                      'w-4 h-4 rounded border-2 flex items-center justify-center transition-colors',
                      allSelected
                        ? 'bg-[var(--gradient-to)] border-[var(--gradient-to)]'
                        : someSelected
                        ? 'bg-[var(--bg-elevated)] border-[var(--gradient-to)]'
                        : 'border-[var(--border-default)]'
                    )}>
                      {(allSelected || someSelected) && <Check className="w-2.5 h-2.5 text-white" strokeWidth={2} />}
                    </div>
                  </div>
                </button>
                <div className="px-4 py-3 flex flex-wrap gap-1.5">
                  {perms.map((p) => (
                    <button
                      key={p.permissionId}
                      onClick={() => toggle(p.permissionId)}
                      disabled={!canEdit}
                      className={cn(
                        'inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-mono font-semibold transition-colors',
                        selected.has(p.permissionId)
                          ? 'bg-[var(--bg-elevated)] text-[var(--text-accent)] border border-[var(--gradient-to)]/30'
                          : 'bg-[var(--bg-base)] text-[var(--text-muted)] border border-[var(--border-subtle)]',
                        canEdit && !selected.has(p.permissionId) && 'hover:border-[var(--border-default)] hover:text-[var(--text-secondary)]',
                        !canEdit && 'cursor-default'
                      )}
                    >
                      {selected.has(p.permissionId) && <Check className="w-2.5 h-2.5" strokeWidth={2} />}
                      {p.code.split('.').slice(1).join('.')}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export const DepartmentsListPage = () => {
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [editingDept, setEditingDept] = useState<Department | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { data: departments = [], isLoading, error } = useQuery({
    queryKey: ['departments'],
    queryFn: () => departmentsService.list(),
  });

  const deleteMutation = useMutation({
    mutationFn: (departmentId: string) => departmentsService.delete(departmentId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['departments'] }),
  });

  const toggleExpand = (departmentId: string) =>
    setExpandedId((prev) => (prev === departmentId ? null : departmentId));

  return (
    <PageGuard permission="departments.read">
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-1">
              Configuration
            </p>
            <h1 className="font-display text-[32px] font-medium tracking-[-0.02em] text-[var(--text-primary)]">
              Departments
            </h1>
            <p className="text-[14px] text-[var(--text-muted)] mt-0.5">
              Chekit-level departments and their permission sets
            </p>
          </div>
          <PermissionGate permission="departments.create">
            <button
              onClick={() => setShowCreate(true)}
              className="inline-flex items-center gap-2 rounded-full bg-[var(--brand-forest)] px-5 py-2.5 text-[14px] font-semibold text-white transition-all duration-200 hover:brightness-110 hover:shadow-[0_12px_32px_rgba(124,179,66,0.45)] active:brightness-95"
            >
              <Plus className="w-4 h-4" strokeWidth={1.5} />
              Create Department
            </button>
          </PermissionGate>
        </div>

        {/* Departments table */}
        <div className="rounded-3xl bg-white shadow-soft overflow-hidden">
          {isLoading && (
            <div className="p-12 text-center text-[14px] text-[var(--text-muted)]">
              Loading departments...
            </div>
          )}
          {error && (
            <div className="p-12 text-center text-[14px] text-[var(--state-danger)]">
              Failed to load departments.
            </div>
          )}

          {!isLoading && !error && departments.length === 0 && (
            <div className="p-12 text-center text-[14px] text-[var(--text-muted)]">
              No departments defined yet.
            </div>
          )}

          {!isLoading && !error && departments.length > 0 && (
            <div className="divide-y divide-[var(--border-subtle)]">
              {departments.map((dept) => (
                <div key={dept.departmentId}>
                  <div className="flex items-center justify-between px-6 py-4 hover:bg-[var(--bg-base)] transition-colors">
                    <button
                      onClick={() => toggleExpand(dept.departmentId)}
                      className="flex items-center gap-3 flex-1 min-w-0 text-left"
                    >
                      <FolderTree className="w-5 h-5 text-[var(--text-muted)] shrink-0" strokeWidth={1.5} />
                      <div className="min-w-0">
                        <div className="text-[14px] font-semibold text-[var(--text-primary)]">{dept.name}</div>
                        {dept.description && (
                          <div className="text-[13px] text-[var(--text-muted)] truncate">{dept.description}</div>
                        )}
                      </div>
                    </button>

                    <div className="flex items-center gap-1 ml-4 shrink-0">
                      <span className="flex items-center gap-1.5 px-3 py-1.5 text-[13px] text-[var(--text-muted)]">
                        <Users className="w-4 h-4" strokeWidth={1.5} />
                        {dept.memberCount}
                      </span>
                      <span className="flex items-center gap-1.5 px-3 py-1.5 text-[13px] text-[var(--text-muted)]">
                        <KeyRound className="w-4 h-4" strokeWidth={1.5} />
                        {dept.permissions.length}
                      </span>
                      <button
                        onClick={() => toggleExpand(dept.departmentId)}
                        className="w-8 h-8 rounded-full flex items-center justify-center text-[var(--text-muted)] hover:bg-[var(--bg-elevated)] hover:text-[var(--text-accent)] transition-colors"
                        title="Manage permissions"
                      >
                        {expandedId === dept.departmentId
                          ? <ChevronDown className="w-4 h-4" strokeWidth={1.5} />
                          : <ChevronRight className="w-4 h-4" strokeWidth={1.5} />}
                      </button>
                      <PermissionGate permission="departments.update">
                        <button
                          onClick={() => setEditingDept(dept)}
                          className="w-8 h-8 rounded-full flex items-center justify-center text-[var(--text-muted)] hover:bg-[var(--bg-base)] hover:text-[var(--text-primary)] transition-colors"
                          title="Edit department"
                        >
                          <Edit2 className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                      </PermissionGate>
                      <PermissionGate permission="departments.delete">
                        <button
                          onClick={() => {
                            if (confirm(`Delete department "${dept.name}"? This cannot be undone.`)) {
                              deleteMutation.mutate(dept.departmentId);
                            }
                          }}
                          disabled={deleteMutation.isPending}
                          className="w-8 h-8 rounded-full flex items-center justify-center text-[var(--text-muted)] hover:bg-[var(--state-danger-bg)] hover:text-[var(--state-danger)] transition-colors disabled:opacity-50"
                          title="Delete department"
                        >
                          <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                      </PermissionGate>
                    </div>
                  </div>

                  {expandedId === dept.departmentId && (
                    <DeptPermissionsPanel dept={dept} />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {showCreate && <DepartmentFormModal onClose={() => setShowCreate(false)} />}
        {editingDept && <DepartmentFormModal dept={editingDept} onClose={() => setEditingDept(null)} />}
      </div>
    </PageGuard>
  );
};
